import { supabase } from "@/integrations/supabase/client";

export interface InsertGroupPaymentInput {
  payerId: string;
  amount: number;
  discount?: number;
  description: string;
  validityDays: number;
  paidAt: string; // 'yyyy-MM-dd'
  paymentMethod: string;
  /** Extra beneficiaries (excluding the payer). For each, a $0 mirror payment is generated. */
  coveredUserIds?: string[];
  /** Display name of the payer — used in the mirror description so the beneficiary knows who paid. */
  payerDisplayName?: string;
}

/**
 * Inserts the main payment (charged to the payer) and, when there are extra
 * beneficiaries (group categories like 2x1), creates mirror $0 payments for
 * each of them so they get the same validity_days extension without affecting
 * the cash closure (arqueo).
 */
export const insertPaymentWithBeneficiaries = async (input: InsertGroupPaymentInput) => {
  const paidAtIso = new Date(input.paidAt + "T12:00:00").toISOString();
  const coveredIds = Array.from(new Set((input.coveredUserIds || []).filter(Boolean)));

  const { data: mainPayment, error: mainError } = await supabase
    .from("payments")
    .insert({
      user_id: input.payerId,
      amount: input.amount,
      discount: input.discount || 0,
      description: input.description || null,
      validity_days: input.validityDays,
      paid_at: paidAtIso,
      payment_method: input.paymentMethod,
      covered_user_ids: coveredIds,
    } as any)
    .select("id")
    .single();

  if (mainError) throw mainError;

  if (coveredIds.length > 0 && mainPayment) {
    const payerLabel = input.payerDisplayName?.trim() || "otro socio";
    const mirrorRows = coveredIds.map((uid) => ({
      user_id: uid,
      amount: 0,
      discount: 0,
      description: `Beneficio grupal — ${input.description || "categoría"} (pagado por ${payerLabel})`,
      validity_days: input.validityDays,
      paid_at: paidAtIso,
      payment_method: "Cortesía grupal",
      parent_payment_id: mainPayment.id,
    } as any));

    const { error: mirrorError } = await supabase.from("payments").insert(mirrorRows);
    if (mirrorError) throw mirrorError;
  }

  return mainPayment;
};
